import { CreateSimulationGroupRequest, Simulation, SimulationGroup } from '@thive/fddm-types';

import { ErrorMessageServiceConnector } from '@/modules/api/ErrorMessageServiceConnector/ErrorMessageServiceConnector';

export type SimulationValidationResult = {
  valid: boolean;
  reasonKey?: string;
};

export class SimulationUtil {
  private static GENERAL_SIMULATION_ERROR_CODE = 'SE0018';

  // eslint-disable-next-line no-useless-constructor
  private constructor() {
    // This is a utility class, so we prevent instantiation by making the constructor private.
  }

  public static getNextSimulationName(records: SimulationGroup[]): string {
    let addition = 1;
    while (records.map((record) => record.name).includes(`Run ${addition}`)) {
      addition++;
    }

    return `Run ${addition}`;
  }

  public static isGroup(simulation: SimulationGroup): boolean {
    return (
      simulation.config.vehicleRanges.find((range) => range.range && range.range.length > 1) !==
      undefined
    );
  }

  public static async getErrorMessage(group: SimulationGroup): Promise<string | undefined> {
    let message: string | undefined;

    const errors = group.errors
      ? await Promise.all(
          group.errors.map(async (error) => {
            let result: string | undefined;

            const notification =
              await ErrorMessageServiceConnector.getInstance().getNotificationDetails(error.code);

            if (notification) {
              result = SimulationUtil.formatErrorMessage(notification.title, error.args);
            }

            if (
              error.code === SimulationUtil.GENERAL_SIMULATION_ERROR_CODE &&
              error.args.length === 1
            ) {
              const internalError = error.args[0];
              if (internalError.toLocaleUpperCase().indexOf('DEADLOCK') !== -1) {
                result += ' [DEADLOCK]';
              }
            }

            return result;
          }),
        )
      : undefined;

    if (errors && errors.length > 0) {
      message = errors.join(',\n');
    }

    return message;
  }

  public static isRunningSimulation(simulation: Simulation): boolean {
    return (
      simulation.status === 'PREPARING' ||
      simulation.status === 'SCHEDULED' ||
      simulation.status === 'RUNNING'
    );
  }

  public static isRunningGroup(group: SimulationGroup): boolean {
    return (
      group.status === 'PREPARING' || group.status === 'SCHEDULED' || group.status === 'RUNNING'
    );
  }

  public static formatErrorMessage(template: string, args: (string | number)[]): string {
    return template.replace(/\{(\d+)\}/g, (match, index) => {
      const value = args[index];
      return value !== undefined ? String(value) : match;
    });
  }

  public static createFleetTrackerUrl(
    projectId: string,
    alternativeId: string,
    revisionId: string,
    simulationRunId: string,
    floorPlanRevisionIdOfSimulation: string,
    isBeta: boolean,
  ): string {
    const tracker = isBeta ? 'visualization' : 'tracker';

    return `/${projectId}/builder/${alternativeId}/version/${revisionId}/${tracker}/${simulationRunId}/${floorPlanRevisionIdOfSimulation}`;
  }

  public static isValid(draft: CreateSimulationGroupRequest): SimulationValidationResult {
    if (!draft) {
      return { valid: false, reasonKey: 'simulation.errors.no_simulation' };
    }
    const { name, config } = draft;

    if (config.duration === 0) return { valid: false, reasonKey: 'simulation.duration_zero' };

    if (!name) return { valid: false, reasonKey: 'simulation.name_required' };

    if (!config.vehicleRanges.every((item) => item.range?.every((value) => value > 0)))
      return { valid: false, reasonKey: 'simulation.invalid_vehicle_ranges' };

    if (config.uvtOverrides) {
      if (
        !config.uvtOverrides.maxForwardSpeedPerc ||
        config.uvtOverrides.maxForwardSpeedPerc <= 0 ||
        !config.uvtOverrides.maxReverseSpeedPerc ||
        config.uvtOverrides.maxReverseSpeedPerc <= 0 ||
        !config.uvtOverrides.loadHandlingDurationPerc ||
        config.uvtOverrides.loadHandlingDurationPerc <= 0
      ) {
        return { valid: false, reasonKey: 'simulation.invalid_uvt_values' };
      }
    }

    return { valid: true };
  }

  public static getFileNameFromUrl(url: string): string {
    // First check if there's a filename query parameter
    const fileNameMatch = url.match(/filename=([^&]*)/);
    if (fileNameMatch && fileNameMatch[1]) {
      return fileNameMatch[1];
    }

    // Fall back to extracting filename from URL path
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1].split('?')[0];
  }
}
